import type { Movie } from "@/mocks/movies";

interface MovieHeroProps {
  movie: Movie;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onShare: (platform: string) => void;
  shareOpen: boolean;
  onToggleShare: () => void;
}

export default function MovieHero({
  movie,
  isFavorite,
  onToggleFavorite,
  onShare,
  shareOpen,
  onToggleShare,
}: MovieHeroProps) {
  const infoRows = [
    { label: "导演", value: movie.director, icon: "ri-vidicon-line" },
    { label: "主演", value: movie.cast.join(" / "), icon: "ri-user-star-line" },
    { label: "类型", value: movie.genres.join(" · "), icon: "ri-price-tag-3-line" },
    { label: "地区", value: movie.region, icon: "ri-earth-line" },
    { label: "上映年份", value: String(movie.year), icon: "ri-calendar-line" },
    { label: "片长", value: `${movie.duration} 分钟`, icon: "ri-time-line" },
  ];

  return (
    <section className="relative w-full">
      {/* backdrop */}
      <div className="absolute inset-0 h-[280px] md:h-[380px] overflow-hidden">
        <img
          src={movie.backdrop}
          alt={`${movie.title} 剧照`}
          title={`${movie.title} - 电影详情`}
          className="w-full h-full object-top object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/20 via-background-50/70 to-background-50"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-10 pt-24 md:pt-32 pb-8">
        <div className="max-w-[1440px] mx-auto">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            {/* poster */}
            <div className="w-[180px] md:w-[280px] shrink-0 rounded-lg overflow-hidden border border-background-200 bg-background-100 mx-auto md:mx-0">
              <div className="w-full aspect-[2/3]">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 高清海报`}
                  title={`${movie.title} - 高清电影海报`}
                  className="w-full h-full object-top object-cover"
                />
              </div>
            </div>

            {/* info */}
            <div className="flex-1 min-w-0 flex flex-col">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {movie.tags.slice(0, 3).map((t) => (
                  <span
                    key={t}
                    className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
                {movie.title}
              </h1>
              <p className="mt-2 text-sm md:text-base text-primary-600 font-heading">
                {movie.tagline}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-4">
                <div className="flex items-baseline gap-1">
                  <span className="font-heading font-bold text-3xl md:text-4xl text-accent-600">
                    {movie.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-foreground-500">/ 10</span>
                </div>
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }, (_, i) => (
                    <i
                      key={i}
                      className={`text-sm ${
                        i < Math.round(movie.rating / 2)
                          ? "ri-star-fill text-accent-500"
                          : "ri-star-line text-foreground-300"
                      }`}
                    ></i>
                  ))}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={onToggleFavorite}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
                    isFavorite
                      ? "bg-primary-500 text-background-50"
                      : "border border-primary-300 text-primary-600 hover:bg-primary-50"
                  }`}
                >
                  <i className={isFavorite ? "ri-heart-fill" : "ri-heart-line"}></i>
                  {isFavorite ? "已收藏" : "收藏"}
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={onToggleShare}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-300 hover:text-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                  {shareOpen && (
                    <div className="absolute left-0 top-full mt-2 z-30 w-44 bg-background-50 border border-background-200 rounded-lg p-2">
                      <button
                        type="button"
                        onClick={() => onShare("copy")}
                        className="w-full text-left px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 cursor-pointer flex items-center gap-2"
                      >
                        <i className="ri-link"></i>
                        复制链接
                      </button>
                      <button
                        type="button"
                        onClick={() => onShare("weibo")}
                        className="w-full text-left px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 cursor-pointer flex items-center gap-2"
                      >
                        <i className="ri-weibo-line"></i>
                        分享到微博
                      </button>
                      <button
                        type="button"
                        onClick={() => onShare("wechat")}
                        className="w-full text-left px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 cursor-pointer flex items-center gap-2"
                      >
                        <i className="ri-wechat-line"></i>
                        分享到微信
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {infoRows.map((r) => (
                  <div key={r.label} className="flex items-start gap-2 text-sm">
                    <i className={`${r.icon} text-primary-500 mt-0.5 shrink-0`}></i>
                    <span className="text-foreground-500 shrink-0">{r.label}</span>
                    <span className="text-foreground-800 break-words">{r.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}